import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Dropdown, Tag } from '@arco-design/web-react';
import { IconClose } from '@arco-design/web-react/icon';
import { styled } from 'styled-components';

import { type Resource, type TagFilter, isFilters } from '../types';
import { getOptionText, parseTag } from '../utils';
import { Droplist } from './droplist';

export interface TagInputProps {
  filters: TagFilter[];
  resource?: Resource[];
  values?: Record<string, any>;
  onChange?: (values: Record<string, any>) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  /** Dropdown 挂载容器，未传时默认挂载到 TAG_FILTER_INPUT_BOX */
  getPopupContainer?: (node: HTMLElement) => Element;
}

export function TagInput({ filters, resource, values, onChange, placeholder, style, getPopupContainer }: TagInputProps) {
  const [tags, setTags] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const removeValueByTag = useCallback(
    (tag: string) => {
      const parsed = parseTag(tag);
      if (!parsed) return;

      const filter = filters.find((f) => isFilters(f) && f.label === parsed.label);
      if (!filter) return;

      // 父组件常用 {...旧值, ...新值} 合并查询条件，删除 key 会被旧值回填；
      // 显式置为 undefined，交给 cleanObject 等清理逻辑真正移除该筛选项。
      onChange?.({ ...values, [filter.key]: undefined });
    },
    [filters, onChange, values],
  );

  // 判断当前输入状态是否应该显示下拉栏
  const shouldShowDropdown = useMemo(() => {
    if (!inputValue.trim()) return true;

    const parsed = parseTag(inputValue);
    if (!parsed) {
      // 输入框有值但没匹配到标签格式
      // 如果存在 defaultSearch: true 且非隐藏的 input filter，则不显示下拉栏
      const defaultFilter = filters.find((f) => isFilters(f) && f.type === 'input' && 'defaultSearch' in f && f.defaultSearch && !f.hidden);
      if (defaultFilter) {
        return false; // 有默认搜索 filter，不显示下拉栏
      }
      return true; // 没有默认搜索 filter，显示筛选菜单
    }

    const filter = filters.find((f) => isFilters(f) && f.label === parsed.label);
    if (!filter) return true; // 匹配到标签但找不到对应的filter，显示筛选菜单

    if (filter.type === 'input') return false; // input类型不显示下拉栏

    return true; // select 和 multiple-select 类型显示下拉栏
  }, [inputValue, filters]);

  // 控制下拉栏显示状态
  useEffect(() => {
    isFocused && setIsDropdownOpen(shouldShowDropdown);
  }, [isFocused, shouldShowDropdown]);

  // 初始化 tags 和 values
  useEffect(() => {
    if (values) {
      const newTags: string[] = [];
      Object.entries(values).forEach(([key, value]) => {
        const filter = filters.find((f) => f.key === key);
        // 跳过 hidden: true 的过滤器，不参与标签展示，只处理 Filters 类型
        // 数值 0 和布尔 false 同样是有效筛选值，标签展示不能按 truthy 判断。
        if (filter && value !== undefined && value !== null && value !== '' && isFilters(filter) && !filter.hidden) {
          switch (filter.type) {
            case 'input':
              newTags.push(`${filter.label}: ${value}`);
              break;
            case 'select': {
              const resourceItem = resource?.find((r) => r.key === key);
              const option = resourceItem?.options?.find((o) => o.value === value);
              if (option) {
                newTags.push(`${filter.label}: ${getOptionText(option)}`);
              }
              break;
            }
            case 'multiple-select': {
              const resourceItem = resource?.find((r) => r.key === key);
              // 兼容数组和字符串格式
              const valueArray = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
              const selectedOptions = resourceItem?.options?.filter((o) => valueArray.includes(o.value));
              if (selectedOptions && selectedOptions.length > 0) {
                const labels = selectedOptions.map((o) => getOptionText(o)).join(',');
                newTags.push(`${filter.label}: ${labels}`);
              }
              break;
            }
          }
        }
      });
      setTags(newTags);
    }
  }, [values, filters, resource]);

  // 容器点击时聚焦输入框
  const handleWrapperClick = () => {
    inputRef.current?.focus();
  };

  // 输入值变化处理函数
  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  // 输入框事件回调
  const handleInputEventChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange(e.target.value);
  };

  // 添加标签的函数
  const addTag = useCallback(
    (tag: string, key: string, value: any) => {
      // 检查对应的过滤器是否为隐藏的
      const filter = filters.find((f) => f.key === key);

      const parsedTag = parseTag(tag);
      const existingTagIndex = tags.findIndex((t) => parseTag(t)?.label === parsedTag?.label);

      let newTags: string[];
      if (filter && isFilters(filter) && filter.hidden) {
        // 如果是隐藏的过滤器，不添加到标签中，只更新 values
        newTags = tags;
      } else {
        // 非隐藏过滤器的正常逻辑
        if (existingTagIndex !== -1) {
          // 如果已存在相同 label 的标签，替换它
          newTags = [...tags];
          newTags[existingTagIndex] = tag;
        } else {
          // 如果不存在，则添加新标签
          newTags = [...tags, tag];
        }
      }

      setTags(newTags);

      // 更新 values
      const newValues = { ...values, [key]: value };
      onChange?.(newValues);

      setInputValue('');
      // 强制关闭下拉栏
      setIsDropdownOpen(false);
      // 让输入框失焦，防止下拉栏闪动
      // inputRef.current?.blur()
    },
    [tags, values, onChange, filters],
  );

  // 删除标签的函数
  const removeTag = useCallback(
    (tagToRemove: string) => {
      setTags((prevTags) => prevTags.filter((t) => t !== tagToRemove));
      removeValueByTag(tagToRemove);
    },
    [removeValueByTag],
  );

  // 点击标签进入二次编辑态：标签回填到输入框，筛选值先从 values 中移除。
  const editTag = useCallback(
    (tagToEdit: string) => {
      setTags((prevTags) => prevTags.filter((t) => t !== tagToEdit));
      setInputValue(tagToEdit);
      setIsDropdownOpen(false);
      removeValueByTag(tagToEdit);

      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    },
    [removeValueByTag],
  );

  // 清除所有内容
  const clearAll = useCallback(() => {
    setTags([]);
    setInputValue('');
    setIsDropdownOpen(false);

    // 显式清空 Filters 类型的值，保留 Schemas 类型的值。
    const newValues: Record<string, any> = { ...values };
    filters.forEach((filter) => {
      if (isFilters(filter)) {
        newValues[filter.key] = undefined;
      }
    });
    Object.entries(values || {}).forEach(([key, value]) => {
      const filter = filters.find((f) => f.key === key);
      if (filter && !isFilters(filter)) {
        // 保留 Schemas 类型的值
        newValues[key] = value;
      }
    });
    onChange?.(newValues);

    inputRef.current?.blur();
  }, [onChange, values, filters]);

  // 点击回车
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && inputRef.current) {
        const value = inputRef.current.value.trim();

        if (!value) return;

        const parsed = parseTag(value);

        if (parsed) {
          // 用户输入了标签格式（如 "名称: xxx"）
          const filter = filters.find((f) => isFilters(f) && f.label === parsed.label);
          if (filter && filter.type === 'input') {
            addTag(value, filter.key, parsed.value);
            return;
          }
        } else {
          // 用户只是输入了普通内容，没有使用标签格式
          // 查找设置了 defaultSearch: true 且非隐藏的 input 类型 filter
          const defaultFilter =
            filters.find((f) => isFilters(f) && f.type === 'input' && 'defaultSearch' in f && f.defaultSearch && !f.hidden) || filters?.[0];

          if (defaultFilter && isFilters(defaultFilter)) {
            // 创建默认搜索标签（addTag 会自动处理覆盖逻辑）
            const tagValue = `${defaultFilter.label}: ${value}`;
            addTag(tagValue, defaultFilter.key, value);
            return;
          }
        }
      }
    },
    [addTag, filters],
  );

  return (
    <TagInputWrapper
      onClick={handleWrapperClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={style}
    >
      <TagBox>
        {tags.map((tag) => (
          <EditableTag
            key={tag}
            closable
            onClick={() => {
              editTag(tag);
            }}
            onClose={(e) => {
              e.stopPropagation();
              removeTag(tag);
            }}
          >
            {tag}
          </EditableTag>
        ))}
        <InputBox id="TAG_FILTER_INPUT_BOX">
          <Dropdown
            trigger={[]}
            position="bl"
            popupVisible={isDropdownOpen}
            onVisibleChange={(b) => {
              if (shouldShowDropdown && isFocused) {
                setIsDropdownOpen(b);
              }
            }}
            getPopupContainer={getPopupContainer || (() => document.getElementById('TAG_FILTER_INPUT_BOX') || document.body)}
            droplist={
              <Droplist
                inputValue={inputValue}
                onInputChange={handleInputChange}
                tags={tags}
                filters={filters}
                resource={resource}
                values={values}
                onAddTag={addTag}
                onCloseDropdown={() => setIsDropdownOpen(false)}
              />
            }
          >
            <UserInput
              ref={inputRef}
              placeholder={placeholder}
              autoComplete="off"
              value={inputValue}
              onChange={handleInputEventChange}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                setIsFocused(true);
                // 聚焦时根据当前状态决定是否显示下拉栏
                if (shouldShowDropdown) {
                  setIsDropdownOpen(true);
                }
              }}
              onBlur={() => {
                setIsFocused(false);
              }}
              onClick={() => {
                // 点击时根据当前状态决定是否显示下拉栏
                if (shouldShowDropdown) {
                  setIsDropdownOpen(true);
                }
              }}
            />
          </Dropdown>
        </InputBox>
      </TagBox>
      <ClearButton
        onClick={clearAll}
        style={{
          flexShrink: 0,
          visibility: (isFocused || isHovered) && (tags.length > 0 || inputValue.trim()) ? 'visible' : 'hidden',
        }}
      />
    </TagInputWrapper>
  );
}

/** ============================ styles ==================================== */
const TagInputWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 2px;
  border: 1px solid var(--color-border-2);
  padding: 0px 4px;
  box-sizing: border-box;
  min-height: 32px;
  position: relative;

  &:focus-within,
  &:hover {
    border-color: rgb(var(--primary-6));
  }
`;

const TagBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  flex: 1;
  align-items: flex-start;
  padding: 2px 0;
`;

const EditableTag = styled(Tag)`
  cursor: pointer;
`;

const InputBox = styled.div`
  flex: 1;
  align-self: stretch;
  position: relative;
  z-index: 1;
  background: #fff;
  padding: 0 8px;
  z-index: 10;
`;

const UserInput = styled.input`
  width: 100%;
  height: 100%;
  min-width: 100px;
  outline: none;
  border: none;
`;

const ClearButton = styled(IconClose)`
  cursor: pointer;
  color: var(--color-text-2);
  border-radius: 50%;
  padding: 1px;
  margin-right: 4px;

  &:hover {
    color: inherit;
    background-color: var(--color-fill-2);
  }
`;
